import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TokenLogo from './TokenLogo';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SyncIcon from '@mui/icons-material/Sync';

// Styled Components
const PaymentInfoContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1.2),
  position: 'relative',
  animation: 'slideUp 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
  '@keyframes slideUp': {
    from: {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    }
  }
}));

const PaymentAmountSection = styled(Box)(({ theme, ownerState }) => {
  const isUrgent = Boolean(ownerState?.isUrgent);
  return ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2.3, 3),
  backgroundColor: '#ffffff',
  borderRadius: 20,
  border: `1px solid ${isUrgent ? 'rgba(255, 152, 0, 0.2)' : 'rgba(103, 126, 234, 0.12)'}`,
  boxShadow: isUrgent 
    ? '0 6px 24px rgba(255, 152, 0, 0.15)' 
    : '0 4px 16px rgba(103, 126, 234, 0.08)',
  marginBottom: theme.spacing(1.3),
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  position: 'relative',
  background: isUrgent 
    ? 'linear-gradient(135deg, #fff4e6 0%, #ffffff 100%)'
    : 'linear-gradient(135deg, #f8faff 0%, #ffffff 100%)',
  '&::after': {
    content: '"Click to copy"',
    position: 'absolute',
    bottom: 8,
    right: 12,
    fontSize: '0.65rem',
    color: 'rgba(0, 0, 0, 0.3)',
    opacity: 0,
    transition: 'opacity 0.2s ease',
    pointerEvents: 'none',
  },
  '&:hover::after': {
    opacity: isUrgent ? 0 : 0.6,
  },
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: isUrgent 
      ? '0 8px 30px rgba(255, 152, 0, 0.15)' 
      : '0 6px 20px rgba(103, 126, 234, 0.08)',
  },
  '&:active': {
    transform: 'scale(0.995)',
  }
  });
});

const AmountDisplay = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

const PrimaryAmount = styled(Typography)(({ theme }) => ({
  fontSize: '1.75rem',
  fontWeight: 700,
  color: theme.palette.text.primary,
  letterSpacing: '-0.03em',
  lineHeight: 1.1,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.5rem',
  }
}));

const SecondaryInfo = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  fontWeight: 500,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8rem',
  }
}));

const CopyIconButton = styled(IconButton)(({ theme, ownerState }) => {
  const show = Boolean(ownerState?.show);
  return ({
  opacity: show ? 1 : 0.3,
  transform: show ? 'scale(1)' : 'scale(0.9)',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  backgroundColor: show ? 'rgba(103, 126, 234, 0.1)' : 'rgba(0, 0, 0, 0.04)',
  width: 36,
  height: 36,
  '&:hover': {
    opacity: 1,
    backgroundColor: 'rgba(103, 126, 234, 0.15)',
    transform: 'scale(1)',
  },
  [theme.breakpoints.down('sm')]: {
    width: 32,
    height: 32,
  }
  });
});

const TimerSection = styled(Box)(({ theme, ownerState }) => {
  const isUrgent = Boolean(ownerState?.isUrgent);
  const isCritical = Boolean(ownerState?.isCritical);
  return ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.3, 2.5),
  backgroundColor: isCritical ? '#fff5f5' : isUrgent ? '#fff8f0' : '#f8faff',
  borderRadius: 16,
  border: `1px solid ${isCritical ? 'rgba(244, 67, 54, 0.15)' : isUrgent ? 'rgba(255, 152, 0, 0.15)' : 'rgba(103, 126, 234, 0.1)'}`,
  marginTop: theme.spacing(-0.3),
  marginBottom: theme.spacing(1.2),
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  transition: 'all 0.3s ease',
  animation: isCritical ? 'pulse 1.5s ease-in-out infinite' : 'none',
  });
});

const TimerText = styled(Typography)(({ theme, ownerState }) => {
  const isUrgent = Boolean(ownerState?.isUrgent);
  const isCritical = Boolean(ownerState?.isCritical);
  return ({
  fontSize: '0.875rem',
  fontWeight: isUrgent ? 600 : 500,
  color: isCritical ? theme.palette.error.main : isUrgent ? '#ff9800' : theme.palette.text.secondary,
  fontVariantNumeric: 'tabular-nums',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8rem',
  }
  });
});

const CheckPaymentSection = styled(Box)(({ theme, ownerState }) => {
  const status = ownerState?.status;
  const isConfirmed = status === 'confirmed';
  const isChecking = status === 'checking';
  const isNotFound = status === 'not_found';
  const background = isConfirmed
    ? '#e8f5e9'
    : isNotFound
      ? '#fff8f0'
      : '#f8faff';
  const border = isConfirmed
    ? 'rgba(76, 175, 80, 0.2)'
    : isNotFound
      ? 'rgba(255, 152, 0, 0.25)'
      : 'rgba(103, 126, 234, 0.1)';
  return ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1.2),
  padding: theme.spacing(1.2, 2.4),
  backgroundColor: background,
  borderRadius: 16,
  border: `1px solid ${border}`,
  marginTop: theme.spacing(-0.3),
  marginBottom: theme.spacing(1.2),
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  transition: 'all 0.3s ease',
  opacity: isChecking ? 0.9 : 1
  });
});

const CheckPaymentButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  borderRadius: 12,
  padding: theme.spacing(0.6, 2),
  backgroundColor: '#ffffff',
  boxShadow: '0 2px 6px rgba(103, 126, 234, 0.12)',
  '&:hover': {
    backgroundColor: '#f5f7ff'
  }
}));

const CheckPaymentText = styled(Typography)(({ theme, ownerState }) => {
  const status = ownerState?.status;
  const isConfirmed = status === 'confirmed';
  const isNotFound = status === 'not_found';
  return ({
  fontSize: '0.875rem',
  fontWeight: isConfirmed ? 600 : 500,
  color: isConfirmed
    ? theme.palette.success.main
    : isNotFound
      ? '#f57c00'
      : theme.palette.text.secondary,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8rem',
  }
  });
});

// Helper Functions
const formatTokenAmount = (amount, symbol) => {
  if (!amount) return '0';
  
  const num = parseFloat(amount);
  
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(2)}M ${symbol}`;
  } else if (num >= 1000) {
    return `${num.toLocaleString()} ${symbol}`;
  } else if (num < 0.001) {
    // For very small numbers, show up to 8 significant digits
    return `${num.toFixed(8).replace(/\.?0+$/, '')} ${symbol}`;
  } else {
    // For normal numbers, show up to 6 significant digits
    return `${parseFloat(num.toFixed(6))} ${symbol}`;
  }
};

const formatUSDRate = (rate) => {
  if (!rate) return '$0.00';
  
  const num = parseFloat(rate);
  
  if (num < 0.01) {
    return `$${num.toFixed(6).replace(/\.?0+$/, '')}`;
  } else {
    return `$${num.toFixed(2)}`;
  }
};

const calculateUSDTotal = (amount, rate) => {
  const total = parseFloat(amount) * parseFloat(rate);
  return total.toFixed(2);
};

const normalizeTokenId = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const PaymentInfo = ({ 
  tokenDetails, 
  availableTokens, 
  selectedToken, 
  countdown, 
  onAmountCopy,
  checkPayment
}) => {
  const [showCopyButton, setShowCopyButton] = useState(false);

  const handleAmountCopy = async () => {
    try {
      await navigator.clipboard.writeText(tokenDetails.estimatedAmount?.toString() || '0');
      if (onAmountCopy) {
        onAmountCopy('Amount copied to clipboard');
      }
    } catch (error) {
      console.error('Failed to copy:', error);
      if (onAmountCopy) {
        onAmountCopy('Failed to copy amount', 'error');
      }
    }
  };

  const selectedTokenId = normalizeTokenId(selectedToken);
  const tokenInfo = selectedTokenId
    ? availableTokens.find(t => Number(t.id) === selectedTokenId)
    : null;
  const tokenSymbol = tokenInfo?.token_symbol;
  const tokenLogoUri = tokenInfo?.token_uri || tokenInfo?.photo_uri || null;
  const isUrgent = Boolean(countdown && countdown.minutes <= 1);
  const isCritical = Boolean(countdown && countdown.minutes === 0 && countdown.seconds <= 30);
  const shouldShowCopyButton = showCopyButton || isUrgent;
  const showCheckPayment = Boolean(checkPayment?.visible);

  return (
    <PaymentInfoContainer>
      <PaymentAmountSection 
        ownerState={{ isUrgent }}
        onClick={handleAmountCopy}
        onMouseEnter={() => setShowCopyButton(true)}
        onMouseLeave={() => setShowCopyButton(false)}
      >
        <AmountDisplay>
          <PrimaryAmount>
            <TokenLogo uri={tokenLogoUri} symbol={tokenSymbol} size={26} />
            {formatTokenAmount(tokenDetails.estimatedAmount, tokenSymbol)}
          </PrimaryAmount>
          <SecondaryInfo>
            {formatUSDRate(tokenDetails.exchangeRate)} per token
            {tokenDetails.estimatedAmount && tokenDetails.exchangeRate && (
              <span style={{ marginLeft: 8, opacity: 0.8 }}>
                ≈ ${calculateUSDTotal(tokenDetails.estimatedAmount, tokenDetails.exchangeRate)} total
              </span>
            )}
          </SecondaryInfo>
        </AmountDisplay>
        
        <CopyIconButton 
          ownerState={{ show: shouldShowCopyButton }}
          onClick={(e) => {
            e.stopPropagation();
            handleAmountCopy();
          }}
          size="small"
        >
          <ContentCopyIcon sx={{ fontSize: 16 }} />
        </CopyIconButton>
      </PaymentAmountSection>

      {showCheckPayment ? (
        <CheckPaymentSection ownerState={{ status: checkPayment?.status }}>
          {checkPayment?.status === 'checking' && (
            <>
              <CircularProgress size={18} sx={{ color: '#667eea' }} />
              <CheckPaymentText ownerState={{ status: 'checking' }}>
                Checking...
              </CheckPaymentText>
            </>
          )}
          {checkPayment?.status === 'confirmed' && (
            <>
              <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
              <CheckPaymentText ownerState={{ status: 'confirmed' }}>
                Confirmed
              </CheckPaymentText>
            </>
          )}
          {checkPayment?.status === 'not_found' && (
            <>
              <SyncIcon sx={{ fontSize: 18, color: '#f57c00' }} />
              <CheckPaymentText ownerState={{ status: 'not_found' }}>
                Not seen yet — try again in ~15s
              </CheckPaymentText>
            </>
          )}
          {(!checkPayment?.status || checkPayment?.status === 'idle') && (
            <CheckPaymentButton
              variant="outlined"
              size="small"
              onClick={checkPayment?.onCheck}
              disabled={checkPayment?.disabled}
              startIcon={<SyncIcon />}
            >
              Check payment
            </CheckPaymentButton>
          )}
        </CheckPaymentSection>
      ) : countdown && (
        <TimerSection 
          ownerState={{ isUrgent, isCritical }}
        >
          {isCritical ? (
            <WarningAmberIcon sx={{ fontSize: 18, color: 'error.main' }} />
          ) : isUrgent ? (
            <WarningAmberIcon sx={{ fontSize: 18, color: '#ff9800' }} />
          ) : (
            <AccessTimeIcon sx={{ fontSize: 18, opacity: 0.7 }} />
          )}
          
          <TimerText 
            ownerState={{ isUrgent, isCritical }}
          >
            {isCritical 
              ? `Expires in ${countdown.seconds}s!`
              : isUrgent
              ? `Expires in ${countdown.minutes}m ${countdown.seconds}s`
              : `Valid for ${countdown.minutes}m ${countdown.seconds}s`
            }
          </TimerText>
        </TimerSection>
      )}
    </PaymentInfoContainer>
  );
};

export default PaymentInfo;
